import { useEffect, useState } from 'react';
import {
  ColumnDef,
  OnChangeFn,
  PaginationState,
  getCoreRowModel,
  useReactTable,
  RowSelectionState,
  RowModel,
} from '@tanstack/react-table';
import { TablePaginationType } from '@/shared/types/common';
import { TableBody, TableFooter, TableHeader } from '../../components';

export interface NavigationConfig<TData> {
  columnId: string;
  getUrl: (row: TData) => string;
}

export interface AppTableProps<TData> {
  data: TData[];
  meta?: TablePaginationType;
  columns: ColumnDef<TData>[];
  pagination?: PaginationState;
  setPagination?: OnChangeFn<PaginationState>;
  getSelectedItems: (rows: TData[]) => void;
  navigationConfig?: NavigationConfig<TData>[];
  onRowClick?: (row: TData) => void;
  getRowClassName?: (row: TData) => string | undefined;
  customTableBody?: (rows: RowModel<TData>['rows']) => React.ReactNode;
  tableMeta?: Record<string, any>;
  /** Название таблицы для скринридера. На экране не видно: в макете строки под него нет. */
  caption?: string;
}

const fallbackData: never[] = [];

export const AppTable = <T,>(props: AppTableProps<T>) => {
  const {
    data,
    meta,
    columns,
    pagination,
    setPagination,
    getSelectedItems,
    navigationConfig,
    onRowClick,
    getRowClassName,
    customTableBody,
    tableMeta,
    caption,
  } = props;
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const table = useReactTable({
    columns,
    data: data || fallbackData,
    manualPagination: true,
    state: { pagination, rowSelection },
    onPaginationChange: setPagination,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    meta: tableMeta,
  });

  useEffect(() => {
    setRowSelection({});
  }, [data]);

  useEffect(() => {
    const selectedRows = table.getSelectedRowModel().rows.map((row) => row.original);
    getSelectedItems(selectedRows);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowSelection]);
  const rows = table.getRowModel().rows;
  return (
    <div className="h-full min-h-0 w-full overflow-hidden rounded-xl border border-grey-modern bg-white">
      <div className="h-full min-h-0 w-full max-h-[70vh] overflow-auto">
        {rows?.length > 0 ? (
          <>
            <table className="min-w-full w-max text-left">
              {caption && <caption className="sr-only">{caption}</caption>}
              <TableHeader getHeaderGroups={table.getHeaderGroups} />
              {customTableBody ? (
                customTableBody(rows)
              ) : (
                <TableBody
                  rows={rows}
                  navigationConfig={navigationConfig}
                  onRowClick={onRowClick}
                  getRowClassName={getRowClassName}
                />
              )}
            </table>
            {meta && pagination && setPagination && (meta.pageCount ?? 1) > 1 && (
              <TableFooter
                total={meta.pageCount}
                pagination={pagination}
                setPagination={setPagination}
              />
            )}
          </>
        ) : (
          <div className="py-30 px-10 w-full text-center h-full">No data available</div>
        )}
      </div>
    </div>
  );
};
